import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  UseFormMethods,
  RegisterOptions,
} from 'react-hook-form';
import InputMask from 'react-input-mask';
import { get } from 'lodash';
import warningImage from '../../../assets/warning.png';
import acceptedImage from '../../../assets/accepted.png';
import {
  getValidationErrorMessage,
  useTriggerValidationOnMount,
} from '../../../utils/validation';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../../../types/utilTypes';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import { Spinner } from '../../Spinner/Spinner';
import styles from './styles.module.scss';

// used to manually set the text field's validation state when a form isn't used
export type TextFieldValidationState =
  | {
      valid: true;
      message?: undefined;
    }
  | {
      valid: false;
      message: string;
    };

type Props<T extends FieldValues> = {
  dataTestId?: string;
  className?: string;
  labelClassName?: string;
  iconClassName?: string;
  showFocusStyle?: boolean;
  showValidation?: boolean;
  label: string;
  addOn?: string;
  tooltip?: string;
  type?: string;
  // allows browser login autofill, only affects password fields
  allowAutofill?: boolean;
  disabled?: boolean;
  loading?: boolean;
  mask?: string;
  width?: 'sm' | 'md' | 'lg' | 'full';
  // Controller has an incompatible definition of onFocus than input
  // so this prop is needed to fix it
  onFocus?: (e: React.FocusEvent) => void;
} & (
  | ({
      // name is required when form is set
      name: FlattenKeys<T> | string;
      form?: UseFormMethods<T>;
      validation?: RegisterOptions;
    } & (
      | {
          skipFormRegistration?: false;
          // the form should control the component when it's set
          value?: undefined;
          validationState?: undefined;
        }
      | {
          // this option is useful for form components that handle the form separately but still require validation
          // such as the Address component
          skipFormRegistration: true;
          value?: string;
          validationState?: TextFieldValidationState;
        }
    ))
  | {
      name?: string;
      form?: undefined;
      skipFormRegistration?: undefined;
      // validation requires a form to be provided
      validation?: undefined;
      value?: string | number;
      validationState?: TextFieldValidationState;
    }
);

export type TextFieldProps<T extends FieldValues> = Props<T> &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof Props<T>>;

export const TextField = <T extends FieldValues>({
  dataTestId,
  className,
  labelClassName,
  iconClassName,
  showFocusStyle,
  label,
  addOn,
  tooltip,
  type,
  allowAutofill,
  disabled,
  loading,
  mask,
  width,
  name,
  form,
  skipFormRegistration,
  validation,
  validationState,
  value,
  showValidation = true,
  ...restProps
}: TextFieldProps<T>) => {
  // if there's data in the form when this component is mounted, trigger validation
  useTriggerValidationOnMount(form, name as any);

  const validationError = get(form?.errors, name as string) as
    | FieldError
    | undefined;

  // manually track the width of the addon to layout the other elements
  const addOnRef = useRef<HTMLDivElement>(null);
  const [addOnWidth, setAddOnWidth] = useState('0px');
  useLayoutEffect(() => {
    if (addOnRef.current) {
      setAddOnWidth(getComputedStyle(addOnRef.current).width);
    } else if (addOnWidth) {
      setAddOnWidth('0px');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOn]);

  const renderInput = () => {
    const inputProps = {
      'data-testid': dataTestId,
      className: classNames(
        styles.input,
        !!validationError && styles.invalid,
        !!showFocusStyle && styles.focus,
      ),
      name: name as string,
      type,
      value,
      disabled: disabled || loading,
      // disable brower login autofill for password fields by default
      // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Preventing_autofilling_with_autocompletenew-password
      ...(type === 'password' && !allowAutofill
        ? {
            autoComplete: 'new-password',
          }
        : {}),
      ...restProps,
    };
    if (mask) {
      if (form && !skipFormRegistration) {
        return (
          <Controller
            control={form.control as Control<Record<string, any>>}
            rules={validation}
            defaultValue=""
            name={name as string}
            render={(controllerProps) => (
              <InputMask
                {...inputProps}
                {...controllerProps}
                onBlur={(event) => {
                  controllerProps.onBlur();
                  inputProps.onBlur?.(event);
                }}
                mask={mask}
              />
            )}
          />
        );
      }
      return <InputMask {...inputProps} mask={mask} />;
    }
    return (
      <input
        {...inputProps}
        ref={skipFormRegistration ? undefined : form?.register(validation)}
      />
    );
  };

  const renderValidation = () => {
    if (loading) {
      return (
        <div className={classNames(styles.icon, iconClassName)}>
          <Spinner className={styles.spinner} />
        </div>
      );
    } else if (validationError || (validationState && !validationState.valid)) {
      return (
        <>
          <div className={classNames(styles.icon, iconClassName)}>
            <img src={warningImage} alt="Warning" />
          </div>
          <span className={styles.warningText}>
            {!!validationError && getValidationErrorMessage(validationError)}
            {!!validationState?.message && validationState.message}
          </span>
        </>
      );
    } else if (
      !!get(form?.formState.touched, name as string) ||
      validationState?.valid
    ) {
      return (
        <div className={classNames(styles.icon, iconClassName)}>
          <img src={acceptedImage} alt="Filled" />
        </div>
      );
    }
    return null;
  };

  const renderAddOn = () => (
    <div ref={addOnRef} className={styles.addOn}>
      <div className={styles.addOnContent}>{addOn}</div>
    </div>
  );

  return (
    <div
      className={classNames(
        styles.container,
        styles[width || 'full'],
        !!addOn && styles.withAddon,
        className,
      )}
      style={{ '--inputAddOnWidth': addOnWidth } as React.CSSProperties}
    >
      {renderInput()}
      <span className={classNames(styles.label, labelClassName)}>
        {label}
        {tooltip && <TooltipIcon content={tooltip} />}
      </span>
      {!!showValidation && renderValidation()}
      {!!addOn && renderAddOn()}
    </div>
  );
};
